import type { RoomDef } from './rooms';
import type { Tile } from './Grid';
export interface Point { x: number; y: number }
export const TILE_SIZE = 64;
export const RADIUS = 10;
export const PLAYER_SPEED = 240;
export const tilePoint = (t: Tile): Point => ({ x: (t.x + .5) * TILE_SIZE, y: (t.y + .5) * TILE_SIZE });
export const pointTile = (p: Point): Tile => ({ x: Math.floor(p.x / TILE_SIZE), y: Math.floor(p.y / TILE_SIZE) });
export const project = (p: Point): Point => ({ x: p.x-p.y, y: (p.x+p.y)/2 });
export const unproject = (p: Point): Point => ({ x: p.x/2+p.y, y: p.y-p.x/2 });
export const distance = (a: Point,b: Point): number => {const p=project({x:b.x-a.x,y:b.y-a.y});return Math.hypot(p.x,p.y);};
function blocks(room: RoomDef,skills: string[]) { return room.objects.filter(o=>o.kind!=='door'&&(!o.requiresSkill||skills.includes(o.requiresSkill))); }
/** Conservative square clearance encloses the circular foot collider. */
export function isFree(room: RoomDef,skills: string[],p: Point,radius=RADIUS): boolean {
 if (!Number.isFinite(p.x)||!Number.isFinite(p.y)||p.x<radius||p.y<radius||p.x>room.cols*TILE_SIZE-radius||p.y>room.rows*TILE_SIZE-radius) return false;
 return !blocks(room,skills).some(o=>p.x>o.x*TILE_SIZE-radius&&p.x<(o.x+o.w)*TILE_SIZE+radius&&p.y>o.y*TILE_SIZE-radius&&p.y<(o.y+o.h)*TILE_SIZE+radius);
}
export function segmentClear(room: RoomDef,skills: string[],a: Point,b: Point,radius=RADIUS): boolean {
 if(!isFree(room,skills,a,radius)||!isFree(room,skills,b,radius))return false;
 // Exact slab test against enlarged footprints: long steps cannot tunnel.
 for(const o of blocks(room,skills)) {
  let lo=0,hi=1;
  for(const [start,delta,min,max] of [[a.x,b.x-a.x,o.x*TILE_SIZE-radius,(o.x+o.w)*TILE_SIZE+radius],[a.y,b.y-a.y,o.y*TILE_SIZE-radius,(o.y+o.h)*TILE_SIZE+radius]]) {
   if(Math.abs(delta)<1e-9){if(start<=min||start>=max){lo=2;break;}}
   else{const t1=(min-start)/delta,t2=(max-start)/delta;lo=Math.max(lo,Math.min(t1,t2));hi=Math.min(hi,Math.max(t1,t2));}
  }
  if(lo<hi && hi>0 && lo<1)return false;
 }
 return true;
}
export function nearestFree(room: RoomDef,skills: string[],p: Point,maxDistance=64): Point|null {
 if(isFree(room,skills,p))return {...p};
 let best:Point|null=null,bestD=Infinity;
 for(let y=16;y<room.rows*TILE_SIZE;y+=16)for(let x=16;x<room.cols*TILE_SIZE;x+=16){const c={x,y},d=Math.hypot(x-p.x,y-p.y);if(d<=maxDistance&&d<bestD&&isFree(room,skills,c)){best=c;bestD=d;}}
 return best;
}
export function recoverPosition(room:RoomDef,skills:string[],p:Point):Point {
 const origin=Number.isFinite(p.x)&&Number.isFinite(p.y)?p:tilePoint(room.spawn);
 return nearestFree(room,skills,origin,Infinity)??tilePoint(room.spawn);
}
export function route(room: RoomDef,skills: string[],start:Point,end:Point):Point[]|null {
 if(!isFree(room,skills,start)||!isFree(room,skills,end))return null;
 if(segmentClear(room,skills,start,end))return [{...end}];
 const step=16,cols=room.cols*4,rows=room.rows*4;
 const node=(x:number,y:number)=>({x:16+x*step,y:16+y*step});
 const key=(x:number,y:number)=>y*cols+x;
 const startNodes: {x:number;y:number;cost:number}[]=[];
 for(let y=0;y<rows;y++)for(let x=0;x<cols;x++){const p=node(x,y);if(Math.hypot(p.x-start.x,p.y-start.y)<=32&&segmentClear(room,skills,start,p))startNodes.push({x,y,cost:distance(start,p)});}
 const cost=new Map<number,number>(),parent=new Map<number,number>();
 const open=startNodes.map(n=>({...n,priority:n.cost+distance(node(n.x,n.y),end)}));
 for(const n of open)cost.set(key(n.x,n.y),n.cost);
 let found:number|undefined;
 while(open.length){let i=0;for(let j=1;j<open.length;j++)if(open[j].priority<open[i].priority)i=j;const n=open.splice(i,1)[0],k=key(n.x,n.y),p=node(n.x,n.y);if(n.cost!==cost.get(k))continue;
  if(Math.hypot(p.x-end.x,p.y-end.y)<=32&&segmentClear(room,skills,p,end)){found=k;break;}
  for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++){if(!dx&&!dy)continue;const x=n.x+dx,y=n.y+dy;if(x<0||y<0||x>=cols||y>=rows)continue;const q=node(x,y);if(!segmentClear(room,skills,p,q))continue;const nk=key(x,y),c=n.cost+distance(p,q);if(c>=(cost.get(nk)??Infinity))continue;cost.set(nk,c);parent.set(nk,k);open.push({x,y,cost:c,priority:c+distance(q,end)});}
 }
 if(found===undefined)return null;
 const raw:Point[]=[{...end}];let k:number|undefined=found;
 while(k!==undefined){raw.push(node(k%cols,Math.floor(k/cols)));k=parent.get(k);}raw.push({...start});raw.reverse();
 const smooth:Point[]=[];let i=0;while(i<raw.length-1){let j=raw.length-1;while(j>i+1&&!segmentClear(room,skills,raw[i],raw[j]))j--;smooth.push(raw[j]);i=j;}return smooth;
}
export function slide(room:RoomDef,skills:string[],from:Point,delta:Point):Point {
 // Small substeps give wall sliding stable behavior, while each segment is swept.
 const n=Math.max(1,Math.ceil(Math.hypot(delta.x,delta.y)));let p={...from};
 for(let i=0;i<n;i++){const d={x:delta.x/n,y:delta.y/n},q={x:p.x+d.x,y:p.y+d.y};if(segmentClear(room,skills,p,q))p=q;else{const x={x:p.x+d.x,y:p.y};if(segmentClear(room,skills,p,x))p=x;const y={x:p.x,y:p.y+d.y};if(segmentClear(room,skills,p,y))p=y;}}
 return p;
}

/** Small transient actor footprints let navigation avoid people without making permanent walls. */
export function withActors(room:RoomDef,actors:Point[]):RoomDef {
 return {...room,objects:[...room.objects,...actors.map((a,i)=>({id:'actor-'+i,kind:'desk' as const,label:'Actor',x:(a.x-10)/64,y:(a.y-10)/64,w:20/64,h:20/64,z:0,color:0}))]};
}
